/**
 * The booking chip (Book My Test, 5A — https://claude.ai/artifact/JZsxUWzSfGAR92ET3wg274).
 *
 * Where the "Needs you" pill was. It tells the office a customer asked for a
 * test, from any page, without getting in the way:
 *   · quiet grey with no bookings; it keeps its spot (fixed width), so nothing
 *     jumps when one arrives;
 *   · blue when new, amber at half the one-business-day promise, red when
 *     late — the worst one sets the color;
 *   · it glows for about 5 seconds when a booking lands (once per booking,
 *     not on every page load), then stays lit. No sound, no toast, no bell;
 *   · click opens a flyout from the right, over the page. It never opens by
 *     itself and never takes focus. × or Esc closes it, or the chip again;
 *     clicking back into the page leaves it open.
 *
 * The chip and the flyout read ONE server list (GET /api/book-my-test/open),
 * so they can't disagree. It checks every 30 seconds and when you come back
 * to the tab. Office roles only: everyone else gets nothing.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarCheck, Clock, CreditCard, Lock, Mail, MessageSquare, Phone, User, Wrench, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PERMISSIONS } from "@/lib/permissions";
import { useNewRequestsSwitch, type TriesSummary } from "@/lib/new-requests";
import { CallOutcomeButtons, callOutcomeCss } from "@/components/requests/call-outcome-buttons";

export type BookingTone = "blue" | "amber" | "red";

export interface OpenBooking {
    id: string;
    name: string;
    site: string | null;
    createdAt: string;
    tone: BookingTone;
    when: string;
    contact: { channel: "call" | "text" | "email"; phone: string | null; email: string | null; label: string };
    bestTimes: string | null;
    devices: string;
    payment: { state: string | null; label: string; ok: boolean };
    owner: { userId: string; firstName: string } | null;
    forYou: boolean;
    handoffLine: string | null;
    jobId: string | null;
    jobNumber: string | null;
    scheduleLock: string | null;
    requester: string | null;
    /** New requests (migration 718): the call tries and the suggested next step. */
    tries?: TriesSummary;
}

export interface OpenBookingsView {
    count: number;
    tone: "none" | BookingTone;
    label: string;
    items: OpenBooking[];
    viewer: "first_owner" | "promise_owner" | "office";
}

export const BOOKINGS_QUERY_KEY = ["book-my-test", "open"] as const;

/** What should make the chip glow: a booking it hasn't seen, or one that just surfaced for you. */
export function glowKeys(view: OpenBookingsView | undefined): string[] {
    if (!view || !Array.isArray(view.items)) return [];
    return view.items.flatMap((i) => [i.id, ...(i.forYou && i.handoffLine ? [`${i.id}:half`] : [])]);
}

const SEEN_LIMIT = 300;
function seenKey(userId: string | undefined) {
    return `gwc.bookings.seen.${userId ?? "anon"}`;
}
function readSeen(userId: string | undefined): Set<string> {
    try {
        const raw = localStorage.getItem(seenKey(userId));
        return new Set(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
        return new Set();
    }
}
function writeSeen(userId: string | undefined, keys: Set<string>) {
    try {
        localStorage.setItem(seenKey(userId), JSON.stringify([...keys].slice(-SEEN_LIMIT)));
    } catch {
        // private window: the glow just repeats next load
    }
}

function contactHref(c: OpenBooking["contact"]): string | null {
    const digits = (c.phone ?? "").replace(/[^\d+]/g, "");
    if (c.channel === "call" && digits) return `tel:${digits}`;
    if (c.channel === "text" && digits) return `sms:${digits}`;
    if (c.email) return `mailto:${c.email}`;
    if (digits) return `tel:${digits}`;
    return null;
}

const CONTACT_WORD = { call: "Call", text: "Text", email: "Email" } as const;
const CONTACT_ICON = { call: Phone, text: MessageSquare, email: Mail } as const;

export function BookingChip() {
    const { can, user } = useAuth();
    const enabled = can(PERMISSIONS.jobs.schedule);
    const qc = useQueryClient();
    const navigate = useNavigate();
    const newRequests = useNewRequestsSwitch();
    const q = useQuery({
        queryKey: BOOKINGS_QUERY_KEY,
        queryFn: () => api.get<OpenBookingsView>("/api/book-my-test/open"),
        enabled,
        refetchInterval: 30_000,
        refetchOnWindowFocus: true,
        staleTime: 15_000,
        retry: false,
    });
    const [open, setOpen] = useState(false);
    const [glow, setGlow] = useState(false);
    const glowTimer = useRef<number | null>(null);
    const userId = user?.id;

    // Glow once per new booking (and once when one surfaces for you), never on every page load.
    const keys = useMemo(() => glowKeys(q.data), [q.data]);
    useEffect(() => {
        if (!q.data) return;
        const seen = readSeen(userId);
        const fresh = keys.filter((k) => !seen.has(k));
        if (fresh.length === 0) return;
        for (const k of fresh) seen.add(k);
        writeSeen(userId, seen);
        setGlow(false);
        window.requestAnimationFrame(() => setGlow(true));
        if (glowTimer.current) window.clearTimeout(glowTimer.current);
        glowTimer.current = window.setTimeout(() => setGlow(false), 5100);
    }, [keys, q.data, userId]);
    useEffect(() => () => (glowTimer.current ? window.clearTimeout(glowTimer.current) : undefined), []);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    const take = useMutation({
        mutationFn: (id: string) => api.post(`/api/book-my-test/${id}/take`, {}),
        onSettled: () => qc.invalidateQueries({ queryKey: BOOKINGS_QUERY_KEY }),
    });
    const contacted = useMutation({
        mutationFn: (v: { id: string; channel: string }) =>
            api.post(`/api/book-my-test/${v.id}/contacted`, { channel: v.channel }),
    });

    if (!enabled) return null;
    const view = q.data;
    const items = Array.isArray(view?.items) ? view.items : [];
    const tone = q.isError ? "none" : (view?.tone ?? "none");
    const label = q.isError ? "Bookings" : typeof view?.label === "string" ? view.label : "No new bookings";

    return (
        <>
            <button
                type="button"
                className={`bkchip t-${tone}${glow ? " glow" : ""}`}
                aria-label={`Bookings: ${label}`}
                aria-expanded={open}
                aria-haspopup="dialog"
                data-testid="booking-chip"
                onClick={() => setOpen((o) => !o)}
                title={q.isError ? "Couldn't load bookings" : undefined}
            >
                <CalendarCheck size={16} aria-hidden />
                <span className="c">{label}</span>
            </button>
            <aside
                className={`bkfly${open ? " on" : ""}`}
                role="dialog"
                aria-label="New bookings"
                aria-hidden={!open}
                {...((open ? {} : { inert: "" }) as Record<string, string>)}
                data-testid="booking-flyout"
            >
                <div className="bkfly-h">
                    <b>New bookings</b>
                    <button type="button" className="x" aria-label="Close" onClick={() => setOpen(false)}>
                        <X size={16} />
                    </button>
                </div>
                <div className="bkfly-b">
                    <div className="legend" aria-hidden>
                        <span>
                            <i style={{ background: "#B23B36" }} />
                            Late
                        </span>
                        <span>
                            <i style={{ background: "#C9772B" }} />
                            Call soon
                        </span>
                        <span>
                            <i style={{ background: "#004AAD" }} />
                            New
                        </span>
                    </div>
                    {items.length === 0 ? (
                        <p className="empty">No bookings waiting. New ones show up here and on the chip.</p>
                    ) : (
                        items.map((b) => {
                            const href = contactHref(b.contact);
                            const Icon = CONTACT_ICON[b.contact.channel];
                            return (
                                <article key={b.id} className={`bc ${b.tone}`} data-testid="booking-card">
                                    <div className="bc-top">
                                        <div>
                                            <b>{b.name}</b>
                                            {b.site && <span className="s">{b.site}</span>}
                                        </div>
                                        <span className="when">{b.when}</span>
                                    </div>
                                    <div className="bc-facts">
                                        <Wrench size={15} aria-hidden />
                                        <span>{b.devices}</span>
                                        {b.bestTimes && (
                                            <>
                                                <Clock size={15} aria-hidden />
                                                <span>{b.bestTimes}</span>
                                            </>
                                        )}
                                        <CreditCard size={15} aria-hidden />
                                        <span className={b.payment.ok ? "" : "warn"}>{b.payment.label}</span>
                                        {b.requester && (
                                            <>
                                                <User size={15} aria-hidden />
                                                <span>Asked by {b.requester}</span>
                                            </>
                                        )}
                                    </div>
                                    {b.owner && <span className="owner">{b.owner.firstName} has it</span>}
                                    {newRequests.on && b.tries && (
                                        <span className="tries" data-testid="booking-tries">
                                            {b.tries.line}
                                            {b.tries.count > 0 && <> · {b.tries.nextLine}</>}
                                        </span>
                                    )}
                                    {b.handoffLine && <span className="handoff">{b.handoffLine}</span>}
                                    <div className="bc-acts">
                                        {href ? (
                                            <a
                                                className="fb contact"
                                                href={href}
                                                onClick={() => {
                                                    // A tap on a phone number is not a talk: only a
                                                    // text or an email sent counts as contacted here.
                                                    // A call is logged with "How did the call go?".
                                                    if (!href.startsWith("tel:"))
                                                        contacted.mutate({ id: b.id, channel: b.contact.channel });
                                                }}
                                            >
                                                <Icon size={14} aria-hidden />
                                                {CONTACT_WORD[b.contact.channel]} {b.contact.label}
                                            </a>
                                        ) : (
                                            <span className="fb contact" aria-disabled>
                                                No phone or email
                                            </span>
                                        )}
                                        {!b.owner && (
                                            <button
                                                type="button"
                                                className="fb take"
                                                disabled={take.isPending}
                                                onClick={() => take.mutate(b.id)}
                                            >
                                                Take it
                                            </button>
                                        )}
                                        {b.jobId ? (
                                            <button
                                                type="button"
                                                className="fb sched"
                                                disabled={!!b.scheduleLock}
                                                title={b.scheduleLock ?? undefined}
                                                onClick={() => navigate(`/jobs/${b.jobId}?schedule=1`)}
                                            >
                                                {b.scheduleLock && <Lock size={13} aria-hidden />}
                                                Schedule
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                className="fb sched"
                                                onClick={() => navigate("/booking")}
                                            >
                                                Make the job
                                            </button>
                                        )}
                                    </div>
                                    {b.scheduleLock && <span className="lockwhy">{b.scheduleLock}</span>}
                                    {newRequests.on && <CallOutcomeButtons bookingId={b.id} compact />}
                                </article>
                            );
                        })
                    )}
                </div>
            </aside>
        </>
    );
}

/** Scoped to the toolbar (.tbv3). Fixed light card colors inside, like the rest of the toolbar. */
export const bookingChipCss = `
  .tbv3 .bkchip{display:inline-flex;align-items:center;justify-content:center;gap:7px;height:34px;min-width:176px;padding:0 13px;
    border-radius:10px;font-weight:700;font-size:13px;cursor:pointer;white-space:nowrap;border:1px solid;
    transition:background-color .3s,color .3s,border-color .3s;font-variant-numeric:tabular-nums}
  .tbv3 .bkchip.t-none{background:#fff;color:var(--ink-soft);border-color:var(--line);font-weight:600}
  .tbv3 .bkchip.t-blue{background:#004AAD;color:#fff;border-color:#004AAD}
  .tbv3 .bkchip.t-amber{background:#C9772B;color:#fff;border-color:#C9772B}
  .tbv3 .bkchip.t-red{background:#B23B36;color:#fff;border-color:#B23B36}
  .tbv3 .bkchip:focus-visible{outline:3px solid #8fb4ee;outline-offset:2px}
  @keyframes bkglow{0%,100%{box-shadow:0 0 0 0 rgba(0,74,173,0)}50%{box-shadow:0 0 0 7px rgba(0,74,173,.28),0 0 18px 4px rgba(0,74,173,.35)}}
  .tbv3 .bkchip.glow{animation:bkglow 1.66s ease-in-out 3}
  @media (prefers-reduced-motion:reduce){.tbv3 .bkchip.glow{animation:none;box-shadow:0 0 0 4px rgba(0,74,173,.3)}}
  .tbv3 .bkfly{position:fixed;top:0;right:0;bottom:0;width:410px;max-width:100vw;background:#FCFAF6;border-left:1px solid #E7DFD2;
    box-shadow:-18px 0 40px -24px rgba(30,25,15,.45);display:grid;grid-template-rows:auto 1fr;color:#14213a;z-index:60;
    transform:translateX(105%);visibility:hidden;transition:transform .22s ease,visibility 0s linear .22s}
  .tbv3 .bkfly.on{transform:translateX(0);visibility:visible;transition:transform .22s ease}
  @media (prefers-reduced-motion:reduce){.tbv3 .bkfly,.tbv3 .bkfly.on{transition:none}}
  .tbv3 .bkfly-h{display:flex;align-items:center;gap:10px;padding:14px 16px;border-bottom:1px solid #E7DFD2}
  .tbv3 .bkfly-h b{font-size:18px;flex:1;font-weight:750}
  .tbv3 .bkfly-h .x{width:30px;height:30px;border-radius:8px;border:1px solid #E7DFD2;background:#fff;cursor:pointer;display:grid;place-items:center;color:#14213a}
  .tbv3 .bkfly-b{overflow-y:auto;padding:12px 14px 18px;display:flex;flex-direction:column;gap:10px;min-height:0}
  .tbv3 .bkfly-b>*{flex:none}
  .tbv3 .bkfly .legend{display:flex;gap:12px;flex-wrap:wrap;font-size:11.5px;color:#5d6b7e}
  .tbv3 .bkfly .legend span{display:inline-flex;align-items:center;gap:5px}
  .tbv3 .bkfly .legend i{width:10px;height:10px;border-radius:3px;display:inline-block}
  .tbv3 .bkfly .empty{font-size:13px;color:#5d6b7e;padding:14px 4px}
  .tbv3 .bc{background:#fff;border:1px solid #E7DFD2;border-radius:12px;padding:12px 12px 12px 16px;display:grid;gap:7px;position:relative;overflow:hidden}
  .tbv3 .bc::before{content:"";position:absolute;left:0;top:0;bottom:0;width:6px;background:var(--tone,#004AAD)}
  .tbv3 .bc.blue{--tone:#004AAD}.tbv3 .bc.amber{--tone:#C9772B}.tbv3 .bc.red{--tone:#B23B36}
  .tbv3 .bc-top{display:flex;justify-content:space-between;gap:8px;align-items:flex-start}
  .tbv3 .bc-top b{font-size:14.5px;display:block}
  .tbv3 .bc-top .s{font-size:12px;color:#5d6b7e;display:block}
  .tbv3 .bc .when{font-size:11.5px;font-weight:700;padding:2px 8px;border-radius:999px;white-space:nowrap}
  .tbv3 .bc.blue .when{background:#E4EDFA;color:#004AAD}.tbv3 .bc.amber .when{background:#FBEEDD;color:#9E5412}.tbv3 .bc.red .when{background:#F8E3E1;color:#B23B36}
  .tbv3 .bc-facts{display:grid;grid-template-columns:20px 1fr;gap:4px 6px;font-size:12.5px;align-items:center}
  .tbv3 .bc-facts svg{color:#5d6b7e}
  .tbv3 .bc-facts .warn{color:#9E5412;font-weight:700}
  .tbv3 .bc .owner{font-size:12px;font-weight:700;color:#4F7A52}
  .tbv3 .bc .handoff{font-size:12px;font-weight:700;color:#9E5412;background:#FBEEDD;border-radius:7px;padding:4px 8px;justify-self:start}
  .tbv3 .bc-acts{display:flex;gap:6px;flex-wrap:wrap;margin-top:2px}
  .tbv3 .fb{border-radius:8px;padding:7px 11px;font-weight:700;font-size:12.5px;cursor:pointer;display:inline-flex;align-items:center;gap:6px;border:1.5px solid;text-decoration:none}
  .tbv3 .fb.contact{background:#fff;color:#004AAD;border-color:#004AAD}
  .tbv3 .fb.take{background:#fff;color:#2B2620;border-color:#D8CEBD}
  .tbv3 .fb.sched{background:#004AAD;color:#fff;border-color:#004AAD}
  .tbv3 .fb[disabled],.tbv3 .fb[aria-disabled]{background:#F1ECE3;color:#9a9186;border-color:#E7DFD2;cursor:not-allowed}
  .tbv3 .fb:focus-visible{outline:3px solid #8fb4ee;outline-offset:2px}
  .tbv3 .bc .lockwhy{font-size:11.5px;color:#9E5412}
  @media (max-width:720px){.tbv3 .bkchip{min-width:0;flex:1 1 auto}}
  .dark .tbv3 .bkchip.t-none{background:#171410;border-color:#372f25}
  .tbv3 .bc .tries{font-size:12px;color:#5d6b7e}
  ${callOutcomeCss}
`;
